/**
 * Document-lane affordances (ADR-0099): new contracts use the block-built
 * layout ('ir') by default; the previous template flow ('legacy') stays
 * available behind a plain text link.
 */

import { useState } from 'react';
import type { DocumentEngine, TemplateListRow } from '../api.js';

export function defaultEngine(t: TemplateListRow): DocumentEngine {
  return t.ir_available ? 'ir' : 'legacy';
}

export function LegacyCreateLink({
  onClick,
  disabled,
  busy,
}: {
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title="Skapa avtalet med den tidigare dokumentlayouten."
      className="text-[11px] text-stone-500 underline decoration-stone-300 underline-offset-2 hover:text-stone-800 disabled:opacity-50"
    >
      {busy ? 'Skapar…' : 'Skapa med tidigare layout'}
    </button>
  );
}

const BANNER_KEY = 'regta.newLayoutBanner.dismissed';

function bannerDismissed(): boolean {
  try {
    return window.localStorage.getItem(BANNER_KEY) === '1';
  } catch {
    return false;
  }
}

export function NewLayoutBanner() {
  const [hidden, setHidden] = useState(bannerDismissed);
  if (hidden) return null;
  const dismiss = () => {
    setHidden(true);
    try {
      window.localStorage.setItem(BANNER_KEY, '1');
    } catch {
      /* banner just reappears next visit */
    }
  };
  return (
    <div
      role="status"
      className="flex items-start gap-3 border-b border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-950 sm:px-6"
    >
      <p className="min-w-0 flex-1 leading-relaxed">
        <span className="font-semibold">Uppdaterad dokumentlayout.</span>{' '}
        Nya avtal skapas med en layout som går att anpassa mer: ändra texten, byt eller ta bort
        rader och granska ändringarna direkt i dokumentet – även i bilagorna. Den tidigare
        layouten finns kvar via “Skapa med tidigare layout” vid varje mall.
      </p>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Stäng meddelandet"
        className="-my-0.5 shrink-0 rounded px-1.5 text-base leading-none text-sky-700 hover:bg-sky-100 hover:text-sky-950"
      >
        ×
      </button>
    </div>
  );
}

export function contractEditorPath(id: string, engine: DocumentEngine | undefined): string {
  return engine === 'ir' ? `/contracts/${id}/beta` : `/contracts/${id}/edit`;
}
